import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserRound, Pencil, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

export default function Profile() {
  const { t } = useLang();
  const { token, user, updateUser } = useAuth();
  const [me, setMe] = useState(user);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);

  const loadPrivateImage = async (path) => {
    if (!path) {
      setImgSrc(null);
      return;
    }
    try {
      const response = await api.get(path.startsWith("/api/") ? path.slice(4) : path, { responseType: "blob" });
        const url = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(response.data); });
      setImgSrc((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch (e) {
      setImgSrc(null);
    }
  };

  const load = () =>
    api
      .get("/auth/me")
      .then((r) => {
        setMe(r.data);
        updateUser(r.data);
      })
      .catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPrivateImage(me?.photo_url);
    return () => {
      setImgSrc((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    };
  }, [me?.photo_url]);

  const startEdit = () => {
    setForm({
      name: me.name || "",
      address: me.address || "",
      license_number: me.license_number || "",
      pos_id: me.pos_id || "",
      photo_url: me.photo_url,
      password: "",
    });
    setEditing(true);
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("New photo ready — save to apply");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const { data } = await api.put("/auth/profile", payload);
      setMe(data);
      updateUser(data);
      setEditing(false);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    [t("name"), me?.name],
    [t("mobileNumber"), me?.mobile ? `+91 ${me.mobile}` : null],
    [t("address"), me?.address],
    [t("posId"), me?.pos_id || "—"],
    [t("licenceNumber"), me?.license_number],
  ];

  return (
    <div className="max-w-3xl space-y-6" data-testid="customer-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("profileTitle")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("profileSub")}</p>
        </div>
        <button data-testid="edit-profile-btn" className="sf-btn-outline" onClick={startEdit}>
          <Pencil className="h-4 w-4" /> {t("editProfile")}
        </button>
      </div>

      <div className="sf-card p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-6">
          {imgSrc ? (
            <img src={imgSrc} alt="Profile" className="h-28 w-28 rounded-xl object-cover border border-slate-200" data-testid="profile-photo" />
          ) : (
            <div className="h-28 w-28 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
              <UserRound className="h-10 w-10 text-slate-400" />
            </div>
          )}
          <div className="flex-1 grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {rows.map(([k, v]) => (
              <div key={k}>
                <div className="sf-label">{k}</div>
                <div className="text-sm font-semibold text-slate-900 break-words" data-testid={`profile-field-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                  {v || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="edit-profile-modal">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {form.photo_url ? (
                <img src={imgSrc} alt="preview" className="h-16 w-16 rounded-lg object-cover border" />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center">
                  <UserRound className="h-6 w-6 text-slate-400" />
                </div>
              )}
              <label className="sf-btn-outline cursor-pointer" data-testid="change-photo-btn">
                <Upload className="h-4 w-4" /> Change Photo
                <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
              </label>
            </div>
            <div>
              <label className="sf-label">Name</label>
              <input data-testid="edit-name-input" className="sf-input" value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="sf-label">Licence Number</label>
                <input data-testid="edit-license-input" className="sf-input" value={form.license_number || ""} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} />
              </div>
              <div>
                <label className="sf-label">POS ID</label>
                <input data-testid="edit-pos-input" className="sf-input" value={form.pos_id || ""} onChange={(e) => setForm((f) => ({ ...f, pos_id: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="sf-label">Address</label>
              <textarea data-testid="edit-address-input" className="sf-input min-h-20" value={form.address || ""} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
            <div>
              <label className="sf-label">New Password (leave blank to keep current)</label>
              <input data-testid="edit-password-input" type="password" className="sf-input" value={form.password || ""} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" />
            </div>
            <DialogFooter>
              <button className="sf-btn-outline" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button data-testid="save-profile-btn" className="sf-btn-primary" onClick={save} disabled={busy}>
                {busy ? "Saving..." : "Save Changes"}
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
